import { useEffect, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'

// ============================================================
// useAccess — the member's capabilities + location scope for UI gating
// ============================================================
// Reads the backend-authoritative /api/v1/team/me ONCE (module-cached, shared
// across the app) and exposes it so navigation, controls, and protected routes
// can hide what the member can't use. This is CONVENIENCE ONLY — every API route
// enforces the same rules server-side, so a forged/stale value here still gets a
// 403/404 from the backend. Never treat the UI as the security boundary.

export type Capability =
  | 'manage_tracks'
  | 'manage_blocks'
  | 'view_billing'
  | 'manage_billing'
  | 'view_admin'
  | 'manage_admin'
  | 'manage_team'

export interface Access {
  role: string
  active: boolean
  is_owner: boolean
  is_admin: boolean
  can_manage_team: boolean
  capabilities: Partial<Record<Capability, boolean>>
  all_locations: boolean
  location_ids: string[] | null // null ⇒ all locations
}

const API = import.meta.env.VITE_API_URL ?? ''

// Module-level cache so Layout + pages share a single /team/me fetch per session.
let cached: Access | null = null
let inflight: Promise<Access | null> | null = null

async function fetchAccess(token: string | null): Promise<Access | null> {
  const res = await fetch(`${API}/api/v1/team/me`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) return null
  const data = await res.json()
  return (data?.data as Access) ?? null
}

export function useAccess(): { access: Access | null; loading: boolean } {
  const { getToken } = useAuth()
  const [access, setAccess] = useState<Access | null>(cached)
  const [loading, setLoading] = useState(cached === null)

  useEffect(() => {
    let cancelled = false
    if (cached) {
      setAccess(cached)
      setLoading(false)
      return
    }
    ;(async () => {
      try {
        if (!inflight) inflight = getToken().then((t) => fetchAccess(t))
        const me = await inflight
        if (me) cached = me
        if (!cancelled) {
          setAccess(me)
          setLoading(false)
        }
      } catch {
        if (!cancelled) setLoading(false)
      } finally {
        inflight = null
      }
    })()
    return () => {
      cancelled = true
    }
  }, [getToken])

  return { access, loading }
}

/** Does the member hold a capability? Defaults to TRUE while access is still
 *  loading OR unavailable (null), so the UI never flickers a control away from a
 *  legacy/owner user — the backend is the real gate. */
export function hasCap(access: Access | null, cap: Capability): boolean {
  if (!access) return true
  return access.capabilities?.[cap] === true
}
